import { type TableMutation } from "../QueryEvaluator/TableMutation";
import { type JsonPathLiteral } from "../jsonTable/JsonPathLiteral";
import { type SemijoinSpec, type WhereSpec, fromSpec } from "../operatorsV2";
import { type QuerySpec, type TopLevelQuerySpec, contentId } from "../operatorsV2/QuerySpec";
import { type OrderedTopKSpec } from "../operatorsV2/orderedTopK";
import {
	type JsonPathRef,
	type Literal,
	isJsonPathRef,
	jsonpathRef,
} from "../operatorsV2/schema-util";
import {
	type BilinearOperator,
	type ComparisonOperator,
	type DatabaseTables,
	type LinearOperator,
	type Primitive,
	type TableName,
	isLinearOperator,
} from "../types";
import { type Store } from "./Store";

export interface QueryBuilder<Tables extends DatabaseTables> {
	pluck: (table: TableName<Tables>, path: JsonPathLiteral) => JsonPathRef;
}

export type LinearConditionExpression = [
	Primitive | JsonPathRef,
	LinearOperator,
	Primitive | JsonPathRef,
];

const isLinearConditionExpression = (
	condition: unknown
): condition is LinearConditionExpression => {
	return Array.isArray(condition) && condition.length === 3 && isLinearOperator(condition[1]);
};

export type BilinearConditionExpression<Tables extends DatabaseTables> = [
	JsonPathRef,
	BilinearOperator,
	Query<Tables, TableName<Tables>>,
];

export type ConditionExpression<Tables extends DatabaseTables> =
	| LinearConditionExpression
	| BilinearConditionExpression<Tables>;

// export type ConditionExpression<Tables extends DatabaseTables> = [
// 	Primitive | JsonPathRef,
// 	ComparisonOperator,
// 	Primitive | Query<Tables, TableName<Tables>> | JsonPathRef,
// ];

// interface QueryOperators<Tables extends DatabaseTables> {
// 	and: (...conditions: ConditionExpression<Tables>[]) => ConditionExpression<Tables>;
// 	or: (...conditions: ConditionExpression<Tables>[]) => ConditionExpression<Tables>;
// 	cmp: (
// 		field: JsonPathLiteral,
// 		op: ComparisonOperator,
// 		value1: Primitive,
// 		value2?: Primitive
// 	) => ConditionExpression<Tables>;
// }

export type QueryOperatorSpec =
	| { and: QueryOperatorSpec[] }
	| { or: QueryOperatorSpec[] }
	| {
			cmp:
				| [string, ComparisonOperator, Primitive]
				| [string, ComparisonOperator, Primitive, Primitive];
	  };

export type SelectedColumns<Tables extends DatabaseTables> = Record<
	`$${string}`,
	Query<Tables, TableName<Tables>, TableName<Tables>>
>;

// const db = new Store({
// 	movies: {},
// 	directors: {},
// 	roadmapItems: {},
// 	roadmapItemUpvotes: {},
// 	tracks: {},
// 	albums: {},
// });

// db.from("tracks").where(({ pluck }) => [
// 	pluck("tracks", "$.AlbumId"),
// 	"IN",
// 	db.from("albums").where(({ pluck }) => [pluck("albums", "$.ArtistId"), "=", 12]),
// ]);

// db.from("movies")
// 	.where(({ pluck }) => [pluck("movies", "$.title"), "=", "The Hobbit"])
// 	.select({
// 		"$.films": ({ jsonGroupArray }) =>
// 			db
// 				.from("movies")
// 				.where(({ pluck }) => [
// 					pluck("movies", "$.director.name"),
// 					"=",
// 					pluck("directors", "$.name"),
// 				])
// 				.orderBy("$.year")
// 				.select(jsonGroupArray("movies", "$")),
// 	});

// const _spec: WhereSpec = {
// 	type: "where",
// 	comparison: {
// 		left: { type: "jsonpath-table-ref", table: "movies", path: "$.title" },
// 		op: "=",
// 		right: { type: "literal", value: "The Hobbit" },
// 	},
// };

// const _spec2: WhereSpec = {
// 	type: "where",
// 	comparison: {
// 		left: { type: "jsonpath-table-ref", table: "movies", path: "$.title" },
// 		op: "IN",
// 		right: { type: "subquery", subqueryId: "whatever" },
// 	},
// };

export class Query<
	Tables extends Record<string, unknown>,
	LeftTable extends TableName<Tables>,
	UsedTables extends TableName<Tables> = LeftTable,
> {
	private _querySpec: QuerySpec;
	private _subqueries: QuerySpec[];
	private _finalizer: OrderedTopKSpec | undefined;
	private _store: Store<Tables>;

	constructor(_store: Store<Tables>, table: LeftTable) {
		this._querySpec = [fromSpec(table)];
		this._subqueries = [];
		this._store = _store;
	}

	get spec(): TopLevelQuerySpec {
		return {
			spec: this._querySpec,
			subquerySpecs: this._subqueries,
			finalizer: this._finalizer,
		};
	}

	get contentId(): string {
		return contentId(this._querySpec);
	}

	where(arg1: (op: QueryBuilder<Tables>) => ConditionExpression<Tables>): this;
	where(arg1: (op: QueryBuilder<Tables>) => ConditionExpression<Tables>): this {
		const conditionSpec = arg1({
			pluck: (table, path) => ({ type: "jsonpath-table-ref", table, path }),
		});

		let whereSpec: WhereSpec | SemijoinSpec;
		if (isLinearConditionExpression(conditionSpec)) {
			const [left, op, right] = conditionSpec;
			const leftSpec = isJsonPathRef(left)
				? left
				: ({ type: "literal", value: left } satisfies Literal);

			if (isJsonPathRef(right)) {
				whereSpec = {
					type: "where",
					comparison: { left: leftSpec, op, right },
				};
			} else {
				whereSpec = {
					type: "where",
					comparison: { left: leftSpec, op, right: { type: "literal", value: right } },
				};
			}
		} else {
			const [left, , right] = conditionSpec;
			const { spec, subquerySpecs } = right.spec;
			this._subqueries.push(...subquerySpecs);
			this._subqueries.push(spec);

			const subqueryId = contentId(spec);
			whereSpec = {
				type: "semijoin",
				rowKey: left,
				right: { type: "subquery", subqueryId },
			};
		}

		this._querySpec.push(whereSpec);
		return this;
	}

	orderBy(accessor: (op: QueryBuilder<Tables>) => JsonPathRef, direction?: "ASC" | "DESC"): this {
		if (this._finalizer) {
			this._finalizer.orderBy = accessor({
				pluck: (table, path) => ({ type: "jsonpath-table-ref", table, path }),
			});
			this._finalizer.direction = direction ?? this._finalizer.direction;
		} else {
			this._finalizer = {
				type: "orderedTopK",
				orderBy: accessor({
					pluck: (table, path) => ({ type: "jsonpath-table-ref", table, path }),
				}),
				direction,
			};
		}
		return this;
	}

	limit(count: number): this {
		if (this._finalizer) {
			this._finalizer.k = count;
		} else {
			this._finalizer = { type: "orderedTopK", k: count };
		}

		return this;
	}

	select(expr: SelectedColumns<Tables>): Query<Tables, LeftTable, UsedTables> {
		//
		// FIXME: this needs to merge with any existing select statements that already exist.
		//

		const entries = Object.entries(expr) as [
			keyof SelectedColumns<Tables>,
			(typeof expr)[keyof SelectedColumns<Tables>],
		][];
		for (const [key, value] of entries) {
			const { spec, subquerySpecs } = value.spec;

			const whereIndex = spec.findIndex((s) => s.type === "where");
			const where = spec[whereIndex];
			if (whereIndex === -1 || where?.type !== "where") {
				throw new Error("Only equijoins supported");
			}
			const leftKey = where.comparison.left;
			const rightKey = where.comparison.right;
			if (leftKey.type !== "jsonpath-table-ref" || rightKey.type !== "jsonpath-table-ref") {
				throw new Error("Only correlated equijoins supported");
			}

			let leftPath: JsonPathRef;
			let rightPath: JsonPathRef;
			if (leftKey.table === this._querySpec[0].table && rightKey.table === spec[0].table) {
				leftPath = leftKey;
				rightPath = rightKey;
			} else if (
				leftKey.table === spec[0].table &&
				rightKey.table === this._querySpec[0].table
			) {
				leftPath = rightKey;
				rightPath = leftKey;
			} else {
				throw new Error("Only correlated equijoins on two tables supported");
			}

			this._subqueries.push(...subquerySpecs);
			const choppedSubquerySpec = [spec[0]] as QuerySpec;
			this._subqueries.push(choppedSubquerySpec);

			const subqueryId = contentId(choppedSubquerySpec);
			this._querySpec.push({
				type: "leftOuterJoin",
				leftKey: leftPath,
				rightKey: rightPath,
				right: { type: "subquery", subqueryId },
				output: {
					type: "insertAggregateAtPath",
					path: jsonpathRef(this._querySpec[0].table, key),
					aggregation: "to_array",
				},
			});
		}

		return this as Query<Tables, LeftTable, UsedTables>;
	}

	subscribe<T>(onChange: (results: T[], changes: TableMutation<T>[]) => void): () => void {
		return this._store.subscribeQuery<T>(this.spec, onChange);
	}
}
